import React, {useEffect, useMemo, useState} from 'react';
import {Box, Button, MenuItem, Paper, Stack, TextField, Typography} from '@mui/material';
import {templateForCategory, wizardChoicesFor} from '../../../constants/templateAudiences';
import {useNavigate, useParams} from 'react-router-dom';
import FormBuilder from '../../SchoolDashboard/Forms/FormBuilder/FormBuilder';
import CategoryPicker from './CategoryPicker';
import useNotification from '../../../utility/hooks/useNotification';
import FORM_TEMPLATE_TYPE, {FORM_TEMPLATE_TYPE_LABELS} from '../../../constants/FormTemplateType';
import {SELF_SERVICE_TEMPLATE_AUDIENCE_LABELS} from '../../../constants/selfServiceTemplateAudience';
import {
    useCreateSharedFormTemplateMutation,
    useGetFormTemplateCategoriesQuery,
    useGetSharedFormTemplatesQuery,
    useSetSharedFormTemplateHiddenMutation,
    useUpdateSharedFormTemplateMutation
} from '../../../api/services/formTemplateLibraryService';

const FormTemplateBuilderPage = () => {
    const {id} = useParams();
    const templateId = id ? Number(id) : null;
    const navigate = useNavigate();
    const {showErrorNotification, showSuccessNotification} = useNotification();

    const {data: categories = []} = useGetFormTemplateCategoriesQuery();
    const {data: active = []} = useGetSharedFormTemplatesQuery(false);
    const {data: inactive = []} = useGetSharedFormTemplatesQuery(true);
    const [createTemplate] = useCreateSharedFormTemplateMutation();
    const [updateTemplate] = useUpdateSharedFormTemplateMutation();
    const [setHidden] = useSetSharedFormTemplateHiddenMutation();

    const [name, setName] = useState('');
    const [type, setType] = useState(FORM_TEMPLATE_TYPE.regular);
    const [categoryId, setCategoryId] = useState(null);
    // A self-service template is offered to the audience chosen here (AD-2581).
    const [audience, setAudience] = useState('');
    const isSelfService = type === FORM_TEMPLATE_TYPE.selfService;
    const [schema, setSchema] = useState([]);
    const [loadedFor, setLoadedFor] = useState(undefined);

    const everyTemplate = useMemo(() => [...active, ...inactive], [active, inactive]);
    // The same rules the form wizard uses, for the type and category chosen here.
    const offeredTo = useMemo(
        () => wizardChoicesFor(templateForCategory(type, categoryId, categories, isSelfService && audience ? [audience] : [])),
        [type, categoryId, categories, isSelfService, audience]
    );

    useEffect(() => {
        if (loadedFor === templateId || categories.length === 0) {
            return;
        }

        if (templateId === null) {
            setName('');
            setType(FORM_TEMPLATE_TYPE.regular);
            setAudience('');
            setSchema([]);
            setCategoryId(categories.find(category => !category.parentId)?.id ?? null);
            setLoadedFor(null);
            return;
        }

        const template = everyTemplate.find(item => item.id === templateId);

        if (!template) {
            return;
        }

        setName(template.name);
        setType(template.type);
        setAudience(template.type === FORM_TEMPLATE_TYPE.selfService ? (template.audiences?.[0] ?? '') : '');
        setCategoryId(template.categoryId);
        setSchema(typeof template.schema === 'string' ? JSON.parse(template.schema) : template.schema ?? []);
        setLoadedFor(templateId);
    }, [templateId, everyTemplate, categories, loadedFor]);

    const clashingTemplate = useMemo(() => {
        const trimmed = name.trim().toLowerCase();

        if (trimmed.length === 0) {
            return null;
        }

        return everyTemplate.find(item =>
            item.id !== templateId && item.name.trim().toLowerCase() === trimmed) ?? null;
    }, [name, everyTemplate, templateId]);

    const save = async () => {
        if (name.trim().length === 0) {
            showErrorNotification('A name is required');
            return;
        }

        if (!categoryId) {
            showErrorNotification('A category is required');
            return;
        }

        if (isSelfService && !audience) {
            showErrorNotification('An audience is required for a self-service template');
            return;
        }

        if (clashingTemplate) {
            showErrorNotification(`The name ${clashingTemplate.name} is already used by another form template`);
            return;
        }

        const body = {
            name: name.trim(),
            type,
            categoryId,
            // Who a template is offered to comes from its type and category. A self-service template
            // is offered to the audience chosen on it.
            audiences: isSelfService ? [audience] : [],
            schema: JSON.stringify(schema)
        };

        const result = templateId === null
            ? await createTemplate(body)
            : await updateTemplate({templateId, ...body});

        if (result.error) {
            showErrorNotification(result.error.message ?? 'Failed to save the form template');
            return;
        }

        showSuccessNotification('Form template saved');
        navigate('/management/templates');
    };

    const makeClashingActive = async () => {
        const result = await setHidden({templateId: clashingTemplate.id, hidden: false});

        if (result.error) {
            showErrorNotification(result.error.message ?? 'Failed to change the form template');
            return;
        }

        showSuccessNotification(`${clashingTemplate.name} is active again`);
    };

    return (
        <Box sx={{pb: 4}}>
            <Typography variant="h5" py={3}>
                {templateId === null ? 'Form Template Builder' : 'Edit form template'}
            </Typography>

            <Paper sx={{p: 2, mb: 2}} elevation={0} variant="outlined">
                <Stack gap={2}>
                    <Stack direction={{xs: 'column', sm: 'row'}} gap={2}>
                        <TextField
                            fullWidth
                            required
                            size="small"
                            label="Name"
                            data-testid="template-name"
                            value={name}
                            error={Boolean(clashingTemplate)}
                            helperText={clashingTemplate
                                ? `${clashingTemplate.name} already uses this name${clashingTemplate.isHidden ? ', and it is inactive' : ''}`
                                : ' '}
                            onChange={(event) => setName(event.target.value)}
                        />
                        <TextField
                            select
                            fullWidth
                            required
                            size="small"
                            label="Form Type"
                            data-testid="template-type"
                            value={type}
                            helperText=" "
                            onChange={(event) => setType(event.target.value)}
                        >
                            {Object.values(FORM_TEMPLATE_TYPE).map(value => (
                                <MenuItem key={value} value={value}>
                                    {FORM_TEMPLATE_TYPE_LABELS[value]}
                                </MenuItem>
                            ))}
                        </TextField>
                        {isSelfService && (
                            <TextField
                                select
                                fullWidth
                                required
                                size="small"
                                label="Audience"
                                data-testid="template-audience"
                                value={audience}
                                helperText=" "
                                onChange={(event) => setAudience(Number(event.target.value))}
                            >
                                {Object.entries(SELF_SERVICE_TEMPLATE_AUDIENCE_LABELS).map(([value, label]) => (
                                    <MenuItem key={value} value={Number(value)}>{label}</MenuItem>
                                ))}
                            </TextField>
                        )}
                    </Stack>

                    <CategoryPicker
                        categories={categories}
                        value={categoryId}
                        onChange={setCategoryId}
                    />

                    <Typography variant="body2" color="text.secondary" data-testid="template-offered-to">
                        {offeredTo.length > 0
                            ? `The form wizard offers this template to ${offeredTo.join(', ')}.`
                            : 'The form wizard does not offer this template to any form. Choose another form type or category.'}
                    </Typography>

                    {clashingTemplate && (
                        <Stack direction="row" gap={1}>
                            <Button
                                size="small"
                                data-testid="open-clashing-template"
                                onClick={() => navigate(`/management/templates/edit/${clashingTemplate.id}`)}
                            >
                                Open {clashingTemplate.name}
                            </Button>
                            {clashingTemplate.isHidden && (
                                <Button
                                    size="small"
                                    data-testid="make-clashing-active"
                                    onClick={makeClashingActive}
                                >
                                    Make it active
                                </Button>
                            )}
                        </Stack>
                    )}
                </Stack>
            </Paper>

            <FormBuilder
                templateMode
                schema={schema}
                onSchemaChange={setSchema}
            />

            <Stack direction="row" gap={2} sx={{mt: 3}}>
                <Button
                    variant="outlined"
                    data-testid="back-to-active-templates"
                    onClick={() => navigate('/management/templates')}
                >
                    Back to Active Templates
                </Button>
                <Button variant="contained" data-testid="save-template" onClick={save}>
                    Save Template
                </Button>
            </Stack>
        </Box>
    );
};

export default FormTemplateBuilderPage;
